import { AlertTriangle, Box, CheckCircle2, Clock3, Package, Truck } from 'lucide-react'
import { useShippingQuoteAttempt } from '@/api/shipping-calculator'
import type { components } from '@/api/schema'
import { BackButton } from '@/components/back-button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateOnly, formatMoney, formatNumber, formatPercent, formatStatus, parseUTCDate } from '@/lib/utils'

type QuoteOption = components['schemas']['RateOption']
type Arrangement = components['schemas']['QuoteAttemptArrangementEvidence']
type ProviderOffer = components['schemas']['QuoteAttemptProviderOfferEvidence']

function DetailField({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 break-words text-sm font-medium ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <Skeleton className="h-8 w-40" />
      <div className="space-y-2">
        <Skeleton className="h-8 w-72 max-w-full" />
        <Skeleton className="h-5 w-48" />
      </div>
      <Skeleton className="h-52 w-full" />
      <Skeleton className="h-72 w-full" />
    </div>
  )
}

function OfferDesktopRow({ offer }: { offer: ProviderOffer }) {
  const selectedTiers = offer.selected_service_tiers?.map(formatStatus).join(', ')
  return (
    <tr className="border-b last:border-0">
      <td className="px-3 py-3 align-top">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">{offer.carrier_name}</span>
          {offer.selected && <Badge variant="default">{selectedTiers || 'Selected'}</Badge>}
          {!offer.rating_eligible && <Badge variant="secondary">Observed only</Badge>}
          {offer.parse_status !== 'parsed' && <Badge variant="outline">{formatStatus(offer.parse_status)}</Badge>}
        </div>
        <p className="text-xs text-muted-foreground">{offer.provider}</p>
        {offer.scac_code && <p className="mt-1 font-mono text-xs">SCAC {offer.scac_code}</p>}
      </td>
      <td className="px-3 py-3 align-top">
        <p className="font-medium">{offer.service_name}</p>
        {offer.provider_service_code && <p className="font-mono text-xs text-muted-foreground">{offer.provider_service_code}</p>}
        <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{offer.quote_id}</p>
        {offer.parse_reason && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{formatStatus(offer.parse_reason)}</p>}
      </td>
      <td className="px-3 py-3 align-top">
        <Badge variant="outline">{formatStatus(offer.source)}</Badge>
        <p className="mt-2 text-xs text-muted-foreground">
          {offer.transit_days != null ? `${offer.transit_days} days` : 'Transit unavailable'}
        </p>
        {offer.estimated_delivery_date && <p className="text-xs">Delivery {formatDateOnly(offer.estimated_delivery_date)}</p>}
      </td>
      <td className="px-3 py-3 text-right align-top tabular-nums">
        <p>{formatMoney(offer.base_cost)}</p>
        <p className="text-xs text-muted-foreground">Freight {formatMoney(offer.freight_charge)}</p>
        <p className="text-xs text-muted-foreground">Fuel {formatMoney(offer.fuel_surcharge)}</p>
      </td>
      <td className="px-3 py-3 text-right align-top tabular-nums">
        <p>{formatPercent(offer.markup_percent)}</p>
        <p className="text-xs text-muted-foreground">Fee {formatMoney(offer.handling_fee)}</p>
        <p className="text-xs text-muted-foreground">Cap {formatMoney(offer.markup_cap)}</p>
      </td>
      <td className="px-3 py-3 text-right align-top font-semibold tabular-nums">
        {formatMoney(offer.customer_cost)}
        {offer.miles_in_transit && <p className="text-xs font-normal text-muted-foreground">{formatNumber(offer.miles_in_transit)} mi</p>}
        {offer.offer_expiration && <p className="text-xs font-normal text-muted-foreground">Expires {offer.offer_expiration}</p>}
      </td>
    </tr>
  )
}

function OfferMobileCard({ offer }: { offer: ProviderOffer }) {
  const selectedTiers = offer.selected_service_tiers?.map(formatStatus).join(', ')
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="font-medium">{offer.carrier_name}</p>
            {offer.selected && <Badge>{selectedTiers || 'Selected'}</Badge>}
            {!offer.rating_eligible && <Badge variant="secondary">Observed only</Badge>}
            {offer.parse_status !== 'parsed' && <Badge variant="outline">{formatStatus(offer.parse_status)}</Badge>}
          </div>
          <p className="text-sm">{offer.service_name}</p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{offer.provider_service_code || offer.scac_code || offer.provider}</p>
        </div>
        <div className="text-right">
          <p className="font-semibold tabular-nums">{formatMoney(offer.customer_cost)}</p>
          <Badge variant="outline">{formatStatus(offer.source)}</Badge>
        </div>
      </div>
      <p className="mt-2 break-all font-mono text-xs text-muted-foreground">{offer.quote_id}</p>
      {offer.parse_reason && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{formatStatus(offer.parse_reason)}</p>}
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 border-t pt-3">
        <DetailField label="Provider price" value={formatMoney(offer.base_cost)} />
        <DetailField label="Markup" value={formatPercent(offer.markup_percent)} />
        <DetailField label="Handling fee" value={formatMoney(offer.handling_fee)} />
        <DetailField label="Markup cap" value={formatMoney(offer.markup_cap)} />
        <DetailField label="Freight / fuel" value={`${formatMoney(offer.freight_charge)} / ${formatMoney(offer.fuel_surcharge)}`} />
        <DetailField label="Transit" value={offer.transit_days != null ? `${offer.transit_days} days` : '-'} />
        <DetailField label="Delivery" value={formatDateOnly(offer.estimated_delivery_date)} />
        <DetailField label="Distance" value={offer.miles_in_transit ? `${formatNumber(offer.miles_in_transit)} mi` : '-'} />
        {offer.offer_expiration && <DetailField label="Expires" value={offer.offer_expiration} />}
      </dl>
    </div>
  )
}

function ShopifyOption({ option, selectedServiceCode }: { option: QuoteOption; selectedServiceCode: string }) {
  const selected = option.service_code === selectedServiceCode
  const warnings = [...new Set(option.components.flatMap((component) => component.warnings ?? []))]
  return (
    <div className="border-t py-4 first:border-t-0 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{option.service_name}</h3>
            {selected && <Badge>Attempt row</Badge>}
          </div>
          <p className="font-mono text-xs text-muted-foreground">{option.service_code}</p>
          <p className="mt-1 text-sm text-muted-foreground">{option.description}</p>
        </div>
        <p className="text-lg font-semibold tabular-nums">{formatMoney(option.total)}</p>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>Earliest {formatDateOnly(option.min_delivery_date)}</span>
        <span>Latest {formatDateOnly(option.max_delivery_date)}</span>
        <span>{option.components.length} shipment component{option.components.length === 1 ? '' : 's'}</span>
      </div>
      {warnings.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-amber-800 dark:text-amber-300">
          {warnings.map((warning) => <li key={warning}>{warning}</li>)}
        </ul>
      )}
    </div>
  )
}

function ArrangementBlock({ arrangement, index }: { arrangement: Arrangement; index: number }) {
  return (
    <section className="border-t py-5 first:border-t-0 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold">{formatStatus(arrangement.strategy)} arrangement {index + 1}</h3>
          {arrangement.selected && <Badge><CheckCircle2 /> Selected</Badge>}
        </div>
        <div className="text-right text-sm tabular-nums">
          <p className="font-semibold">{formatMoney(arrangement.customer_cost)}</p>
          <p className="text-xs text-muted-foreground">Provider {formatMoney(arrangement.base_cost)}</p>
        </div>
      </div>

      <div className="mt-4 space-y-5">
        {arrangement.groups.map((group) => (
          <div key={group.key} className="rounded-lg border bg-muted/15 p-3 sm:p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 className="font-medium">{group.manufacturer_name}</h4>
                <p className="mt-0.5 font-mono text-xs text-muted-foreground">{group.key}</p>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                <p>Ready {formatDateOnly(group.ready_date)}</p>
                <p>{group.origin_postal_prefix || '-'} · {formatStatus(group.origin_source)} · {formatStatus(group.origin_confidence)}</p>
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {(group.items ?? []).map((item) => `Variant ${item.variant_id} × ${item.quantity}`).join(' · ') || 'No physical items'}
            </p>

            <div className="mt-4 space-y-5">
              {(group.plans ?? []).map((plan) => (
                <div key={`${group.key}-${plan.mode}`} className="border-t pt-4 first:border-t-0 first:pt-0">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      {plan.mode === 'parcel' ? <Package className="h-4 w-4 text-muted-foreground" /> : <Truck className="h-4 w-4 text-muted-foreground" />}
                      <h5 className="font-medium">{plan.mode === 'parcel' ? 'Parcel plan' : 'Freight plan'}</h5>
                      {plan.selected && <Badge variant="outline">Selected plan</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formatNumber(plan.total_weight_lbs)} lb · {plan.handling_unit_count} handling unit{plan.handling_unit_count === 1 ? '' : 's'}
                      {plan.estimated_pallet_count ? ` · ${plan.estimated_pallet_count} pallet${plan.estimated_pallet_count === 1 ? '' : 's'}` : ''}
                    </p>
                  </div>

                  <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {(plan.packages ?? []).map((pkg, packageIndex) => (
                      <div key={`${pkg.product_id ?? 'package'}-${packageIndex}`} className="rounded-md border bg-background px-3 py-2 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{pkg.quantity} package{pkg.quantity === 1 ? '' : 's'}</span>
                          <span>{formatNumber(pkg.weight_lbs)} lb each</span>
                        </div>
                        <p className="mt-1 text-muted-foreground">
                          {formatNumber(pkg.length_in)} × {formatNumber(pkg.width_in)} × {formatNumber(pkg.height_in)} in · Class {pkg.freight_class} · {pkg.stackable ? 'Stackable' : 'Not stackable'}
                        </p>
                      </div>
                    ))}
                  </div>

                  {(plan.provider_offers ?? []).length ? (
                    <div className="mt-4">
                      <div
                        className="hidden overflow-x-auto lg:block"
                        tabIndex={0}
                        aria-label={`${group.manufacturer_name} ${formatStatus(plan.mode)} provider offers`}
                      >
                        <table className="w-full min-w-[980px] text-sm">
                          <thead className="border-y bg-muted/40 text-left text-xs text-muted-foreground">
                            <tr>
                              <th className="px-3 py-2 font-medium">Provider</th>
                              <th className="px-3 py-2 font-medium">Service / quote</th>
                              <th className="px-3 py-2 font-medium">Source / transit</th>
                              <th className="px-3 py-2 text-right font-medium">Provider price</th>
                              <th className="px-3 py-2 text-right font-medium">Markup / fees</th>
                              <th className="px-3 py-2 text-right font-medium">Customer price</th>
                            </tr>
                          </thead>
                          <tbody>{plan.provider_offers?.map((offer) => <OfferDesktopRow key={`${offer.quote_id}-${offer.service_name}`} offer={offer} />)}</tbody>
                        </table>
                      </div>
                      <div className="space-y-2 lg:hidden">
                        {plan.provider_offers?.map((offer) => <OfferMobileCard key={`${offer.quote_id}-${offer.service_name}`} offer={offer} />)}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-muted-foreground">No live provider offers were retained for this plan.</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export function ShippingQuoteAttemptDetailPage({ attemptId }: { attemptId: number }) {
  const quote = useShippingQuoteAttempt(attemptId)

  if (quote.isLoading) return <DetailSkeleton />
  if (quote.isError) {
    return (
      <div className="mx-auto max-w-3xl py-12 text-center">
        <AlertTriangle className="mx-auto h-8 w-8 text-destructive" />
        <h1 className="mt-3 text-xl font-semibold">Quote attempt unavailable</h1>
        <p className="mt-1 text-sm text-muted-foreground">Refresh the page or return to quote history.</p>
      </div>
    )
  }
  const attempt = quote.data
  if (!attempt) {
    return <div className="py-12 text-center text-muted-foreground">Quote attempt not found.</div>
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <BackButton fallback={{ to: '/shipping-calculator' }} fallbackLabel="Back to Shipping" />

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">Quote attempt</h1>
          <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{attempt.request_fingerprint}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge>{formatStatus(attempt.status)}</Badge>
            <Badge variant="outline">{formatStatus(attempt.request_source)}</Badge>
            <Badge variant="outline">{formatStatus(attempt.result_source)}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock3 className="h-4 w-4" />
          {parseUTCDate(attempt.created_at).toLocaleString()}
        </div>
      </header>

      <Card>
        <CardContent className="pt-6">
          <dl className="grid gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
            <DetailField label="Customer price" value={formatMoney(attempt.customer_cost)} />
            <DetailField label="Provider price" value={formatMoney(attempt.base_cost)} />
            <DetailField label="Booked cost" value={formatMoney(attempt.booked_cost)} />
            <DetailField label="Cost variance" value={formatMoney(attempt.cost_variance)} />
            <DetailField label="Subtotal" value={formatMoney(attempt.discounted_subtotal)} />
            <DetailField label="Selected Shopify service" value={attempt.selected_service_code || '-'} mono />
            <DetailField label="Destination" value={`${formatStatus(attempt.region_code)} · ${attempt.destination_postal_prefix || '-'}`} />
            <DetailField label="Latency" value={`${attempt.duration_ms} ms`} />
          </dl>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle><h2>Request and policy</h2></CardTitle></CardHeader>
          <CardContent>
            <dl className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
              <DetailField label="Attempt ID" value={attempt.id} />
              <DetailField label="Policy version" value={attempt.evidence?.policy_version ?? '-'} />
              <DetailField label="Policy record ID" value={attempt.policy_version_id ?? '-'} />
              <DetailField label="Request source" value={formatStatus(attempt.request_source)} />
              <DetailField label="Result source" value={formatStatus(attempt.result_source)} />
              <DetailField label="Currency" value={attempt.currency} />
              <DetailField label="Calibration eligible" value={attempt.calibration_eligible ? 'Yes' : 'No'} />
            </dl>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle><h2>Reconciliation</h2></CardTitle></CardHeader>
          <CardContent>
            <dl className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
              <DetailField label="Shopify order ID" value={attempt.shopify_order_id ?? 'Not matched'} />
              <DetailField label="Selected shipping price" value={formatMoney(attempt.selected_shipping_price)} />
              <DetailField label="Matched" value={attempt.matched_at ? parseUTCDate(attempt.matched_at).toLocaleString() : 'Not matched'} />
              <DetailField label="Reconciled" value={attempt.reconciled_at ? parseUTCDate(attempt.reconciled_at).toLocaleString() : 'Not reconciled'} />
            </dl>
          </CardContent>
        </Card>
      </div>

      {(attempt.warnings ?? []).length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Warnings</p>
              <ul className="mt-1 space-y-1 text-sm">{attempt.warnings?.map((warning) => <li key={warning}>{warning}</li>)}</ul>
            </div>
          </div>
        </div>
      )}

      {attempt.evidence_status !== 'complete' || !attempt.evidence ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <Box className="mx-auto h-6 w-6 text-muted-foreground" />
          <h2 className="mt-2 font-semibold">Provider details unavailable</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {attempt.evidence_status === 'malformed' ? 'This attempt has incomplete saved evidence.' : 'This attempt predates detailed quote evidence.'}
          </p>
        </div>
      ) : (
        <>
          <Card>
            <CardHeader><CardTitle><h2>Shopify options</h2></CardTitle></CardHeader>
            <CardContent>{attempt.evidence.options.map((option) => <ShopifyOption key={option.service_code} option={option} selectedServiceCode={attempt.selected_service_code} />)}</CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle><h2>Shipment groups, plans, and provider offers</h2></CardTitle></CardHeader>
            <CardContent>
              {attempt.evidence.arrangements.length ? attempt.evidence.arrangements.map((arrangement, index) => <ArrangementBlock key={`${arrangement.strategy}-${index}`} arrangement={arrangement} index={index} />) : <p className="text-sm text-muted-foreground">No shipment arrangements were recorded.</p>}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
